import React, { useState, useCallback } from 'react';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import { LoadingSpinner } from '@common/LoadingSpinner';
import { Button } from '@common/Button';
import { logError, logInfo } from '@utils/logger';
import { MapPin, Route } from 'lucide-react';
import POIMarker from '@modules/roadscope/components/map/POIMarker.jsx';

// Get API key directly from environment variable
const GOOGLE_MAPS_API_KEY = 'AIzaSyAdNYCigSTxjhN3zL39QmB6H4nL5-zku7A';

// Define libraries array outside component to prevent recreation
const LIBRARIES = ['places', 'drawing', 'geometry', 'visualization'];

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: true,
  mapTypeControlOptions: {
    position: 7 // Right top
  },
  styles: [
    {
      featureType: "administrative",
      elementType: "all",
      stylers: [{ visibility: "on" }]
    },
    {
      featureType: "landscape",
      elementType: "all",
      stylers: [{ visibility: "on" }]
    },
    {
      featureType: "poi",
      elementType: "all",
      stylers: [{ visibility: "on" }]
    },
    {
      featureType: "road",
      elementType: "all",
      stylers: [{ visibility: "on" }]
    },
    {
      featureType: "transit",
      elementType: "all",
      stylers: [{ visibility: "on" }]
    },
    {
      featureType: "water",
      elementType: "all",
      stylers: [{ visibility: "on" }]
    }
  ]
};

export const StructuresMap = ({ structures = [], route, onRouteChange }) => {
  const [isLoaded, setIsLoaded] = React.useState(!!window.google?.maps);
  const [loadError, setLoadError] = React.useState(null);
  const mapRef = React.useRef(null);
  
  // Check if Google Maps is loaded
  React.useEffect(() => {
    if (window.google?.maps) {
      setIsLoaded(true);
    } else {
      // Wait for Google Maps to load from the main script
      const checkGoogleMaps = setInterval(() => {
        if (window.google?.maps) {
          setIsLoaded(true);
          clearInterval(checkGoogleMaps);
        }
      }, 100);
      
      // Set a timeout to prevent infinite checking
      setTimeout(() => {
        if (!window.google?.maps) {
          setLoadError(new Error('Google Maps failed to load'));
          clearInterval(checkGoogleMaps);
        }
      }, 10000);
      
      return () => clearInterval(checkGoogleMaps);
    }
  }, []);

  const [directionsService, setDirectionsService] = useState(null);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);
  const [waypoints, setWaypoints] = useState([]);
  const [calculating, setCalculating] = useState(false);
  const [routeConfirmed, setRouteConfirmed] = useState(false);
  const [routeResult, setRouteResult] = useState(null);
  const [error, setError] = useState('');

  // Initialize directions service and renderer
  const onMapLoad = useCallback((map) => {
    if (!window.google?.maps) {
      setError('Map services not available');
      setError('Map services not available. Please try refreshing the page.');
      return;
    }

    try {
      // Ensure directions service is available
      if (!window.google.maps.DirectionsService) {
        throw new Error('Directions service not available');
      }

      const service = new window.google.maps.DirectionsService();
      const renderer = new window.google.maps.DirectionsRenderer({
        map,
        suppressMarkers: true
      });
      
      setDirectionsService(service);
      setDirectionsRenderer(renderer);
      logInfo('Directions service initialized');

      // Set initial map bounds to Montreal area
      map.setCenter({ lat: 45.5017, lng: -73.5673 });
      map.setZoom(11);
    } catch (error) {
      logError('Failed to initialize directions service:', error);
      setError('Failed to initialize route planning. Please try again.');
    }
  }, [directionsService, directionsRenderer]);

  const calculateRoute = useCallback(async () => {
    if (!directionsService || !directionsRenderer || waypoints.length < 2) return;

    setError(''); // Clear any previous errors
    if (!window.google?.maps?.DirectionsService) {
      setError('Route planning service not available');
      return;
    }

    setCalculating(true);
    let result;
    try {
      const request = {
        origin: waypoints[0],
        destination: waypoints[waypoints.length - 1],
        waypoints: waypoints.slice(1, -1).map(point => ({
          location: point,
          stopover: true
        })),
        travelMode: google.maps.TravelMode.DRIVING
      };

      result = await directionsService.route(request);
      directionsRenderer.setDirections(result);

      // Store route result
      setRouteResult(result);
      // Extract route points
      const points = result.routes[0].overview_path.map(point => ({
        latitude: point.lat(),
        longitude: point.lng()
      }));

      setRouteConfirmed(false); // Reset confirmation when new route is calculated
      setError(''); // Clear any errors on success
      
      logInfo('Route calculated successfully');
    } catch (error) {
      logError('Failed to calculate route:', error);
      setError('Failed to calculate route. Please try again.');
    } finally {
      setCalculating(false);
    }

    return result;
  }, [directionsService, directionsRenderer, waypoints, onRouteChange]);

  const handleMapClick = useCallback((e) => {
    const newPoint = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng()
    };
    setWaypoints(prev => [...prev, newPoint]);
  }, []);

  if (loadError) {
    return (
      <div className="h-full flex items-center justify-center bg-[#2A2B2E]">
        <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
          Failed to load Google Maps. Please check your internet connection and try again.
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="h-full flex items-center justify-center bg-[#2A2B2E]">
        <div className="flex flex-col items-center gap-4">
          <LoadingSpinner />
          <p className="text-white">Loading Google Maps...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full relative">
      <GoogleMap
        ref={mapRef}
        mapContainerStyle={{ width: '100%', height: '100%' }}
        zoom={12}
        center={{ lat: 45.5017, lng: -73.5673 }}
        options={mapOptions}
        onLoad={onMapLoad}
        onClick={handleMapClick}
      >
        {/* Waypoint Markers */}
        {waypoints.map((point, index) => (
          <Marker
            key={index}
            position={point}
            label={(index + 1).toString()}
          />
        ))}

        {/* Structure Markers */}
        {structures.map((structure) => (
          <div
            key={structure.id}
            className="structure-marker"
            style={{
              position: 'absolute',
              left: '0',
              top: '0',
              width: '0',
              height: '0'
            }}
          >
            {/* Custom marker will be created in useEffect */}
          </div>
        ))}
      </GoogleMap>

      {/* Controls */}
      <div className="absolute top-4 left-4 space-y-2 flex flex-col">
        <Button
          onClick={() => setWaypoints([])}
          className="bg-red-600 hover:bg-red-700 text-white"
          disabled={waypoints.length === 0}
        >
          Clear Waypoints
        </Button>

        <Button
          onClick={calculateRoute}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          disabled={waypoints.length < 2 || calculating || routeConfirmed}
        >
          {calculating ? 'Calculating...' : 'Calculate Route'}
        </Button>
        {!routeConfirmed && waypoints.length >= 2 && !calculating && routeResult && (
          <Button
            onClick={() => {
              setRouteConfirmed(true);
              if (!routeResult) {
                setError('No route calculated yet');
                return;
              }
              onRouteChange({
                points: waypoints.map(wp => ({
                  latitude: wp.lat,
                  longitude: wp.lng
                })),
                distance: routeResult.routes[0].legs.reduce((total, leg) => total + leg.distance.value, 0),
                duration: routeResult.routes[0].legs.reduce((total, leg) => total + leg.duration.value, 0)
              });
            }}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            Confirm Route
          </Button>
        )}
      </div>

      {/* Instructions */}
      {waypoints.length === 0 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-lg">
          Click on the map to add route waypoints
        </div>
      )}
      
      {!routeConfirmed && waypoints.length >= 2 && !calculating && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-lg">
          Calculate and confirm route to see nearby structures
        </div>
      )}

      {error && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-red-500/90 text-white px-4 py-2 rounded-lg shadow-lg">
          {error}
        </div>
      )}
     
     {/* Remove API Key display */}
     <style>{`
       .gm-style-cc {
         display: none !important;
       }
       .gmnoprint.gm-style-cc {
         display: none !important;
       }
       .gm-style div[style*="bottom: 0px"] {
         display: none !important;
       }
       div[style*="background-color: rgba(0, 0, 0, 0.5)"] {
         display: none !important;
       }
       div[style*="color: rgb(255, 255, 255)"] {
         display: none !important;
       }
       .gm-style-pbc {
         display: none !important;
       }
     `}</style>
    </div>
  );
};